export const FormulaireTypeChamp = {
  Label: 0,
  Text: 1,
  Memo: 2,
  Numeric: 3,
  Decimal: 4,
  CheckBox: 5,
  Date: 6,
  DateTime: 7,
  RadioGroup: 8,
  CheckedListBox: 9,
  DropDownList: 10,
  BoldLabel: 11,
  BoldTitle: 12,
  Phone: 13,
  ExpirationMonthYear: 14,
  DateNaissance: 15
};

export function isLabelOrTitleField(field) {
  return field?.typeChamp === FormulaireTypeChamp.Label ||
    field?.typeChamp === FormulaireTypeChamp.BoldLabel ||
    field?.typeChamp === FormulaireTypeChamp.BoldTitle;
}

const getRequiredFieldErrorMessage = (language = 'fr') => {
  return language === 'en' ? '* This field is required' : '* Ce champ est obligatoire';
};
const getMinValueErrorMessage = (min, language = 'fr') => {
  return language === 'en' ? `* The minimum value is ${min}` : `* La valeur minimale est ${min}`;
};
const getMaxValueErrorMessage = (max, language = 'fr') => {
  return language === 'en' ? `* The maximum value is ${max}` : `* La valeur maximale est ${max}`;
};
const getMinLengthErrorMessage = (minLength, language = 'fr') => {
  return language === 'en' ? `* The minimum length is ${minLength} characters` : `* Minimum de ${minLength} caractères`;
};
const getMaxLengthErrorMessage = (maxLength, language = 'fr') => {
  return language === 'en' ? `* The maximum length is ${maxLength} characters` : `* Maximum de ${maxLength} caractères`;
};
const getPhoneErrorMessage = (language = 'fr') => {
  return language === 'en' ? '* Requires a valid phone number' : '* Requiert un téléphone valide';
};

/**
 * Validates a field value and returns an error message if invalid, or null if valid.
 * 
 * @param {Object} field - The field object with typeChamp, requis, min, max, minLength, maxLength properties
 * @param {*} value - The value to validate
 * @param {string} language - Language for validation messages ('fr' or 'en')
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateFieldValue = (field, value, language = 'fr') => {
  if (!field || typeof field !== 'object' || field.typeChamp === undefined) {
    return null;
  }

  // Validate required fields
  if (field.requis) {
    if (field.typeChamp === FormulaireTypeChamp.CheckedListBox) {
      if (!Array.isArray(value) || value.length === 0) {
        return getRequiredFieldErrorMessage(language);
      }
    } else if (field.typeChamp === FormulaireTypeChamp.CheckBox) {
      if (!value) {
        return getRequiredFieldErrorMessage(language);
      }
    } else if (field.typeChamp === FormulaireTypeChamp.ExpirationMonthYear) {
      if (typeof value === 'string') {
        if (!value || value.trim() === '') return getRequiredFieldErrorMessage(language);
      } else if (!value || !value.month || !value.year || value.month === '' || value.year === '') {
        return getRequiredFieldErrorMessage(language);
      }
    } else {
      // For text, numeric, date, radio, dropdown, phone, etc.
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return getRequiredFieldErrorMessage(language);
      }
    }
  }

  // Validate numeric constraints
  if (value && field.typeChamp === FormulaireTypeChamp.Numeric) {
    if (field.min !== undefined && value < field.min) {
      return getMinValueErrorMessage(field.min, language);
    }
    if (field.max !== undefined && value > field.max) {
      return getMaxValueErrorMessage(field.max, language);
    }
  }

  // Validate phone: must be exactly 10 digits when present
  if (field.typeChamp === FormulaireTypeChamp.Phone) {
    const digitsOnly = (value != null && typeof value === 'string' ? value : '').replace(/\D/g, '');
    if (digitsOnly.length > 0 && digitsOnly.length !== 10) {
      return getPhoneErrorMessage(language);
    }
  }

  // Validate string length constraints (minLength and maxLength)
  if (value && typeof value === 'string') {
    if (field.typeChamp !== FormulaireTypeChamp.Phone) {
      if (field.minLength !== undefined && value.length < field.minLength) {
        return getMinLengthErrorMessage(field.minLength, language);
      }
      if (field.maxLength !== undefined && value.length > field.maxLength) {
        return getMaxLengthErrorMessage(field.maxLength, language);
      }
      if (value.length > 999) {
        return language === 'en' ? `* The maximum length is 999 characters` : `* La longueur maximale est de 999 caractères`;
      }
    }
  }

  return null; // Field is valid
};

export const parseListeChoix = (listeChoix) => {
  if (!listeChoix || listeChoix.trim() === '') {
    return [];
  }
  return listeChoix.split(';').map(item => {
    const trimmed = item.trim();
    return { value: trimmed, label: trimmed };
  });
};

export const getListeChoix = (field) => {
  if (field.listeChoix) {
    return parseListeChoix(field.listeChoix);
  }
  return field.listeChoix || [];
};

